/**
 * Персистентный Layout-Shell (волна E, docs/MIGRATION_ROUTER.md §3 п.7).
 *
 * Живёт в __root ПОВЕРХ Outlet и не размонтируется на переходах между
 * роутами — в отличие от старого Layout, который жил внутри
 * CacheDataRoute каждого роута и пересоздавал Header/Footer на каждый
 * переход (в v5 Switch переиспользовал элемент, и Layout жил один раз
 * за сессию; Shell возвращает эту семантику).
 *
 * Особенности роута приходят через staticData.layout (LayoutStaticFlags,
 * см. Router.type.ts); роут без staticData — дефолт: шапка + подвал.
 * Страница рендерится внутри <main> через Outlet (CacheDataRoute теперь
 * рендерит только SEO/мета + компонент страницы, без Layout).
 *
 * Условия рендера скопированы 1:1 из components/Layout/index.tsx.
 */
import { lazy, Suspense, useEffect } from 'react';
import type { FC } from 'react';
import { ClientOnly, Outlet, useMatches } from '@tanstack/react-router';
import Header from './Header';
import Footer from './Footer';
import MobileBottomNav from './MobileBottomNav';
import ProductQuickInfoModalRoot from '@components/Modals/ProductQuickInfoModal/Root';
import VideoModal from '@components/Modals/VideoModal';
import LeaveFeedbackModalRoot from '@components/Modals/LeaveFeedbackModal/Root';
import ScrollToTop from 'react-scroll-up';
import type { LayoutStaticFlags } from '@root/src/Router.type';
import { useLocation } from '@compat/router';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import './index.scss';

const LazyBarMenuHeader = lazy(
  async () => await import('@routes/BarMenu/layout/BarMenuHeader')
);

// порт из dev (мерж 06.08.2026): dev-симулятор авторизации на localhost
const LazyDevAuthSimulator =
  process.env.NODE_ENV !== 'production'
    ? lazy(async () => await import('@components/DevAuthSimulator'))
    : null;

const LayoutShell: FC = observer(() => {
  const { pathname } = useLocation();
  const matches = useMatches();
  const leaf = matches[matches.length - 1];
  const flags: LayoutStaticFlags = leaf?.staticData?.layout ?? {};
  const {
    path = pathname,
    isOnlyMainHeader,
    addMobileMargin,
    landingFooter,
    withoutFooter,
    withoutHeader
  } = flags;

  // notFound рендерится без листового матча (остаётся только __root__)
  const is404 = !leaf || leaf.routeId === '__root__';

  const isProductPage = pathname.split('/').includes('product');
  const isReviewsPage = pathname.split('/').includes('reviews');
  const isBarMenuPage =
    pathname.split('/').includes('bar-menu') ||
    pathname.split('/').includes('barmenu');
  const isOysterBarTestPage = pathname.startsWith('/ustrichnyj-bar-');
  const isBarMenuAnd404 = isBarMenuPage && is404;
  const isCartOrderPage = pathname.startsWith('/cart/order');
  const isMobileBottomNavVisible =
    !isBarMenuAnd404 &&
    !withoutFooter &&
    !isOysterBarTestPage &&
    !isBarMenuPage &&
    !isCartOrderPage;

  useEffect(() => {
    document.body.classList.toggle('is-product-page', isProductPage);
    return () => {
      document.body.classList.remove('is-product-page');
    };
  }, [isProductPage]);

  return (
    <>
      {!isBarMenuAnd404 && !withoutHeader && (
        <Header path={path} isOnlyMainHeader={isOnlyMainHeader} />
      )}

      {isBarMenuPage && (
        <Suspense fallback={null}>
          <LazyBarMenuHeader />
        </Suspense>
      )}

      <main
        className={clsx(
          addMobileMargin && 'add-mobile-margin',
          isMobileBottomNavVisible && 'has-mobile-bottom-nav',
          isBarMenuPage &&
            !pathname.split('/').includes('cart') &&
            'min-h-0 max-w-[767.98px] mx-auto',
          isBarMenuAnd404 && 'w-full h-full bg-[#2f4a59] max-w-none'
        )}
      >
        <Outlet />
        <div
          className={`scroll-top-button-under-all${
            isProductPage ? ' product-page-fixed-elements-position' : ''
          }${
            isReviewsPage ? ' product-page-fixed-elements-position--reviews' : ''
          }`}
        >
          {isOysterBarTestPage ? (
            <button
              className='oyster-bar-scroll-top-button'
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              type='button'
              aria-label='Прокрутить наверх'
            >
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M7 14L12 9L17 14'
                  stroke='white'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>
          ) : (
            <ScrollToTop showUnder={200} duration={1500}>
              <div
                className={clsx(
                  'scroll-top-button',
                  isBarMenuPage && 'scroll-top-button--bar-menu',
                  // порт из dev (мерж 11.08.2026, PR #5942)
                  isBarMenuPage &&
                    pathname.split('/').includes('cart') &&
                    'scroll-top-button--bar-menu-cart'
                )}
              />
            </ScrollToTop>
          )}
        </div>
      </main>

      {isMobileBottomNavVisible && <MobileBottomNav />}

      {!isBarMenuAnd404 && !withoutFooter && !isOysterBarTestPage && (
        <Footer
          path={path}
          className={landingFooter ? 'landing-footer' : ''}
        />
      )}

      <ProductQuickInfoModalRoot />
      <VideoModal />
      <LeaveFeedbackModalRoot />
      {LazyDevAuthSimulator && (
        <ClientOnly fallback={null}>
          {typeof window !== 'undefined' &&
            location.host === 'localhost:8080' && (
              <Suspense fallback={null}>
                <LazyDevAuthSimulator />
              </Suspense>
            )}
        </ClientOnly>
      )}
    </>
  );
});

export default LayoutShell;
